import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Plus, Pencil, Loader2, Sparkles, Power, PowerOff } from "lucide-react";
import { getServiceIcon, ICON_OPTIONS, suggestServiceIcon } from "@/lib/serviceIcons";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { listSucursales } from "@/services/sucursalesService";
import DashboardLayout from "@/components/DashboardLayout";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { CurrencyField } from "@/components/ui/forms";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const FORMAS = ["Independiente", "Estacionamiento", "Valet"];
const emptyForm = { nombre: "", categoria: "", descripcion: "", precio_base: "", forma_venta: "Independiente", icono: "", iconoManual: false, estatus: true };

export default function Servicios() {
  const [items, setItems] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [matriz, setMatriz] = useState({}); // { id_sucursal: {activo, precio_sucursal} }
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, su] = await Promise.all([
        api.get("/servicios?incluir_inactivos=true"),
        listSucursales(),
      ]);
      setItems(s.data);
      setSucursales(su.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sucName = Object.fromEntries(sucursales.map((s) => [s.id_sucursal, s.nombre]));

  const openCreate = () => {
    setEditing(null); setForm(emptyForm);
    setMatriz(Object.fromEntries(sucursales.map((s) => [s.id_sucursal, { activo: false, precio_sucursal: "" }])));
    setOpen(true);
  };
  const openEdit = (sv) => {
    setEditing(sv);
    setForm({ nombre: sv.nombre, categoria: sv.categoria || "", descripcion: sv.descripcion || "", precio_base: String(sv.precio_base), forma_venta: sv.forma_venta, icono: sv.icono || suggestServiceIcon(sv.nombre), iconoManual: !!sv.icono, estatus: sv.estatus });
    const m = Object.fromEntries(sucursales.map((s) => [s.id_sucursal, { activo: false, precio_sucursal: "" }]));
    (sv.sucursales || []).forEach((c) => { m[c.id_sucursal] = { activo: c.activo, precio_sucursal: c.precio_sucursal != null ? String(c.precio_sucursal) : "" }; });
    setMatriz(m); setOpen(true);
  };

  const setRow = (id, patch) => setMatriz((m) => ({ ...m, [id]: { ...m[id], ...patch } }));

  const save = async () => {
    if (!form.nombre.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      const sucs = Object.entries(matriz)
        .filter(([, v]) => v.activo || (v.precio_sucursal !== "" && v.precio_sucursal != null))
        .map(([id, v]) => ({
          id_sucursal: id, activo: !!v.activo,
          precio_sucursal: v.precio_sucursal === "" || v.precio_sucursal == null ? null : Number(Number(v.precio_sucursal).toFixed(2)),
        }));
      const payload = {
        nombre: form.nombre.trim(), categoria: form.categoria || null, descripcion: form.descripcion || null,
        precio_base: Number(Number(form.precio_base || 0).toFixed(2)), forma_venta: form.forma_venta,
        icono: form.icono || null,
        sucursales: sucs,
      };
      if (editing) {
        await api.put(`/servicios/${editing.id_servicio}`, { ...payload, estatus: form.estatus });
        toast.success("Servicio actualizado");
      } else {
        await api.post("/servicios", payload);
        toast.success("Servicio creado");
      }
      setOpen(false); setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doToggle = async (sv) => {
    try {
      if (sv.estatus) await api.delete(`/servicios/${sv.id_servicio}`);
      else await api.put(`/servicios/${sv.id_servicio}`, { estatus: true });
      toast.success(sv.estatus ? "Servicio desactivado" : "Servicio reactivado");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <DashboardLayout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2">Catálogo del inquilino</p>
            <h1 className="font-display text-4xl font-black tracking-tight">Servicios adicionales</h1>
          </div>
          <button onClick={openCreate} data-testid="create-servicio-btn"
            className="btn-hard h-11 px-5 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm flex items-center gap-2">
            <Plus className="h-4 w-4" /> Nuevo servicio
          </button>
        </div>

        <div className="bg-white border border-[#e4e4e7]">
          {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
          : items.length === 0 ? <div className="p-16 text-center text-[#52525B]"><Sparkles className="h-8 w-8 mx-auto mb-3 opacity-40" /><p className="text-sm">Sin servicios registrados.</p></div>
          : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="servicios-table">
              <thead><tr className="border-b border-[#e4e4e7] text-left">
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Servicio</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Categoría</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Forma de venta</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Precio base</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Estatus</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase text-right">Acciones</th>
              </tr></thead>
              <tbody>
                {items.map((sv) => (
                  <tr key={sv.id_servicio} className="border-b border-[#f0f0f1] hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-5 py-4 font-semibold"><span className="inline-flex items-center gap-2">{(() => { const I = getServiceIcon(sv.icono); return <span className="h-7 w-7 shrink-0 inline-flex items-center justify-center border border-[#e4e4e7] bg-[#FAFAFA]" data-testid={`servicio-row-icon-${sv.id_servicio}`}><I className="h-4 w-4 text-[#0A0A0A]" strokeWidth={1.8} /></span>; })()}{sv.nombre}</span></td>
                    <td className="px-5 py-4 text-[#52525B]">{sv.categoria || "—"}</td>
                    <td className="px-5 py-4"><span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#FAFAFA] border border-[#e4e4e7]">{sv.forma_venta}</span></td>
                    <td className="px-5 py-4 font-mono">${Number(sv.precio_base).toFixed(2)}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold ${sv.estatus ? "text-[#047857]" : "text-[#B45309]"}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${sv.estatus ? "bg-[#10B981]" : "bg-[#F59E0B]"}`} />{sv.estatus ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right"><div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(sv)} data-testid={`edit-servicio-${sv.id_servicio}`} className="h-8 w-8 inline-flex items-center justify-center border border-[#e4e4e7] hover:border-[#0A0A0A] transition-colors duration-200"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setConfirm(sv)} data-testid={`toggle-servicio-${sv.id_servicio}`}
                        className={`h-8 px-3 inline-flex items-center gap-1.5 border border-[#e4e4e7] text-[11px] font-bold uppercase tracking-wider transition-colors duration-200 ${sv.estatus ? "text-[#B45309] hover:border-[#F59E0B]" : "text-[#047857] hover:border-[#10B981]"}`}>
                        {sv.estatus ? <><PowerOff className="h-3.5 w-3.5" /> Desactivar</> : <><Power className="h-3.5 w-3.5" /> Reactivar</>}
                      </button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </motion.div>

      <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto border-l-[#0A0A0A]">
          <SheetHeader><SheetTitle className="font-display text-2xl font-black tracking-tight">{editing ? "Editar servicio" : "Nuevo servicio"}</SheetTitle><SheetDescription>Configura el servicio y su disponibilidad por sucursal.</SheetDescription></SheetHeader>
          <div className="space-y-5 mt-6">
            <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Nombre</Label>
              <Input value={form.nombre} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, nombre: v, icono: f.iconoManual ? f.icono : suggestServiceIcon(v) })); }} data-testid="servicio-nombre" className="h-11 rounded-none border-[#0A0A0A]" /></div>
            <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Icono</Label>
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-2" data-testid="servicio-icono-picker">
                {ICON_OPTIONS.map(({ key, label, Icon }) => (
                  <button key={key} type="button" title={label} data-testid={`servicio-icono-${key}`}
                    onClick={() => setForm((f) => ({ ...f, icono: key, iconoManual: true }))}
                    className={`aspect-square flex items-center justify-center border transition-colors duration-150 ${form.icono === key ? "border-[#0A0A0A] bg-[#0A0A0A] text-[#FFCC00]" : "border-[#e4e4e7] text-[#52525B] hover:border-[#0A0A0A]"}`}>
                    <Icon className="h-5 w-5" strokeWidth={1.8} />
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[#A1A1AA]">Se sugiere automáticamente por el nombre; puedes cambiarlo.</p></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Categoría</Label>
                <Input value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} data-testid="servicio-categoria" className="h-11 rounded-none border-[#0A0A0A]" /></div>
              <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Precio base</Label>
                <CurrencyField value={form.precio_base} onChange={(v) => setForm({ ...form, precio_base: v })} data-testid="servicio-precio" className="h-11 rounded-none border-[#0A0A0A]" /></div>
            </div>
            <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Forma de venta</Label>
              <Select value={form.forma_venta} onValueChange={(v) => setForm({ ...form, forma_venta: v })}>
                <SelectTrigger data-testid="servicio-forma" className="h-11 rounded-none border-[#0A0A0A]"><SelectValue /></SelectTrigger>
                <SelectContent>{FORMAS.map((f) => <SelectItem key={f} value={f} data-testid={`forma-${f}`}>{f}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Descripción</Label>
              <Input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} data-testid="servicio-desc" className="h-11 rounded-none border-[#0A0A0A]" /></div>
            {editing && (
              <div className="flex items-center justify-between border border-[#e4e4e7] p-3">
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Activo (estatus)</Label>
                <Switch checked={form.estatus} onCheckedChange={(v) => setForm({ ...form, estatus: v })} data-testid="servicio-estatus" className="data-[state=checked]:bg-[#10B981]" />
              </div>
            )}

            <div className="border border-[#e4e4e7] p-4 space-y-3">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] block">Disponibilidad por sucursal</Label>
              {sucursales.length === 0 ? <p className="text-xs text-[#52525B]">No hay sucursales.</p> : (
                <div className="space-y-2" data-testid="servicio-matriz">
                  {sucursales.map((s) => {
                    const row = matriz[s.id_sucursal] || { activo: false, precio_sucursal: "" };
                    return (
                      <div key={s.id_sucursal} className="flex items-center gap-3 bg-[#FAFAFA] border border-[#e4e4e7] px-3 py-2">
                        <Switch checked={row.activo} onCheckedChange={(v) => setRow(s.id_sucursal, { activo: v })} data-testid={`sv-activo-${s.id_sucursal}`} className="data-[state=checked]:bg-[#0A0A0A]" />
                        <span className="text-sm font-medium flex-1 truncate">{s.nombre}</span>
                        <CurrencyField placeholder="Precio excep." value={row.precio_sucursal}
                          onChange={(v) => setRow(s.id_sucursal, { precio_sucursal: v })}
                          data-testid={`sv-precio-${s.id_sucursal}`} className="h-9 w-28 rounded-none border-[#0A0A0A] text-sm" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <button onClick={save} disabled={saving} data-testid="servicio-save-btn"
              className="w-full h-12 bg-[#0A0A0A] text-white font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#27272A] transition-colors duration-200 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Guardar cambios" : "Crear servicio"}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o) setConfirm(null); }}
        title={confirm?.estatus ? "¿Desactivar servicio?" : "¿Reactivar servicio?"}
        description={confirm?.estatus ? `Se desactivará "${confirm?.nombre}". No se elimina; podrás reactivarlo.` : `Se reactivará "${confirm?.nombre}".`}
        confirmLabel={confirm?.estatus ? "Desactivar" : "Reactivar"}
        danger={confirm?.estatus}
        onConfirm={() => { const c = confirm; setConfirm(null); if (c) doToggle(c); }}
      />
    </DashboardLayout>
  );
}
